import { ChangeDetectionStrategy, Component, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { CalculatorTTMData } from '../../../shared/models/calculator-ttm-data';
import { BaseCalculatorComponent } from '../base-calculator.component';

interface MoneyRunResults {
  buyPrice: number;
  stickerPrice: number;
  cashFlowYield: number;
  analysis: string;
}

@Component({
    standalone: true,
    selector: 'app-money-a-run-calculator',
    imports: [
        CommonModule,
        ReactiveFormsModule
    ],
    templateUrl: './money-a-run-calculator.component.html',
    styleUrls: ['./money-a-run-calculator.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MoneyARunCalculatorComponent extends BaseCalculatorComponent implements OnInit {
  moneyRunForm!: FormGroup;
  isCalculated = false;
  isLoading = signal(false);
  results: MoneyRunResults = {
    buyPrice: 0,
    stickerPrice: 0,
    cashFlowYield: 0,
    analysis: ''
  };

  constructor(private fb: FormBuilder) {
    super();
  }

  ngOnInit(): void {
    this.initializeForm();
  }

  private initializeForm(): void {
    this.moneyRunForm = this.fb.group({
      growthRate: [null, [Validators.required, Validators.min(0), Validators.max(100)]],
      fcfPerShare: [null, [Validators.required, Validators.min(0)]],
      targetYears: [8, [Validators.required, Validators.min(1), Validators.max(30)]]
    });
  }

  incrementTargetYears(): void {
    const currentValue = this.moneyRunForm.get('targetYears')?.value || 8;
    if (currentValue < 30) {
      this.moneyRunForm.patchValue({ targetYears: currentValue + 1 });
    }
  }

  decrementTargetYears(): void {
    const currentValue = this.moneyRunForm.get('targetYears')?.value || 8;
    if (currentValue > 1) {
      this.moneyRunForm.patchValue({ targetYears: currentValue - 1 });
    }
  }

  /**
   * Populate form fields with TTM data from the calculator data service
   */
  protected override populateFormWithTtmData(ttmData: CalculatorTTMData): void {
    const updates: Partial<{ fcfPerShare: number; growthRate: number }> = {};

    // Pre-populate FCF per share (freeCashFlowPerShare from backend)
    if (ttmData.freeCashFlowPerShare !== null && ttmData.freeCashFlowPerShare !== undefined) {
      updates.fcfPerShare = ttmData.freeCashFlowPerShare;
    }

    // Pre-populate growth rate (appliedGrowthRate from backend)
    if (ttmData.appliedGrowthRate !== null && ttmData.appliedGrowthRate !== undefined) {
      updates.growthRate = ttmData.appliedGrowthRate;
    }

    // Update the form with the new values
    if (Object.keys(updates).length > 0) {
      this.moneyRunForm.patchValue(updates);
      this.logger.debug('Money-a-Run Calculator pre-populated with TTM data:', updates);
    }
  }

  calculateMoneyRun(): void {
    if (this.moneyRunForm.invalid) return;

    this.isLoading.set(true);

    // Get form values
    const growthRate = (this.moneyRunForm.get('growthRate')?.value || 0) / 100; // Convert percentage to decimal
    const fcfPerShare = this.moneyRunForm.get('fcfPerShare')?.value || 0;
    const targetYears = this.moneyRunForm.get('targetYears')?.value || 8;

    // Simulate calculation delay
    setTimeout(() => {
      // Calculate using the exact formula provided
      const buyPrice = this.calculateBuyPrice(fcfPerShare, growthRate, targetYears);
      const stickerPrice = buyPrice * 2; // Double the buy price since we already discounted to 8 years
      const cashFlowYield = (fcfPerShare / buyPrice) * 100;
      const analysis = this.generateAnalysis(growthRate * 100, cashFlowYield, targetYears);

      this.results = {
        buyPrice: Math.round(buyPrice * 100) / 100,
        stickerPrice: Math.round(stickerPrice * 100) / 100,
        cashFlowYield: Math.round(cashFlowYield * 10) / 10,
        analysis
      };

      this.isCalculated = true;
      this.isLoading.set(false);
    }, 1000);
  }

  private calculateBuyPrice(fcfPerShare: number, growthRate: number, targetYears: number): number {
    // Use decimal math; convert to double only for Pow, then back.
    const g = growthRate;
    const onePlusG = 1.0 + g;
    const pow = Math.pow(onePlusG, targetYears);

    // Price = FCF0 * ((1+g) * ((1+g)^n - 1) / g)
    const factor = (onePlusG * (pow - 1.0)) / g;

    const price = fcfPerShare * factor;
    return Math.round(price * 100) / 100;
  }

  private generateAnalysis(growthRate: number, cashFlowYield: number, targetYears: number): string {
    if (growthRate <= 0) {
      return "No growth expected - This investment may not generate adequate returns through cash flows alone.";
    } else if (growthRate <= 5) {
      return `Conservative growth (${growthRate.toFixed(1)}%) - Suitable for income-focused investors seeking steady cash flows over ${targetYears} years. The ${cashFlowYield.toFixed(1)}% cash flow yield provides predictable returns.`;
    } else if (growthRate <= 10) {
      return `Moderate growth (${growthRate.toFixed(1)}%) - Good balance of growth and cash flow generation. The ${cashFlowYield.toFixed(1)}% yield suggests solid cash flow coverage for the ${targetYears}-year payoff period.`;
    } else if (growthRate <= 15) {
      return `Strong growth (${growthRate.toFixed(1)}%) - Excellent cash flow growth potential. The ${cashFlowYield.toFixed(1)}% yield indicates robust cash generation that should accelerate over the ${targetYears}-year investment horizon.`;
    } else {
      return `High growth (${growthRate.toFixed(1)}%) - Exceptional growth rate that may not be sustainable long-term. Consider using a more conservative growth estimate. Current ${cashFlowYield.toFixed(1)}% yield assumes continued high growth over ${targetYears} years.`;
    }
  }

  clearForm(): void {
    this.moneyRunForm.reset();
    this.moneyRunForm.patchValue({
      targetYears: 8 // Reset to default
    });
    this.isCalculated = false;
    this.results = {
      buyPrice: 0,
      stickerPrice: 0,
      cashFlowYield: 0,
      analysis: ''
    };
  }
}

